/*
 * The words on each project page. Written from what the live sites show, in the first person: I built them.
 * Images follow a fixed naming scheme in /assets/images/projects/<id>/ (see scripts/project-shots/README.md):
 * cover.webp, desktop-1..4.webp, mobile-1..3.webp.
 */
export interface Delivered {
    title: string;
    text: string;
}

export interface ProjectContent {
    /** Where the client is based, when the live site says so. */
    place?: string;
    /** One statement under the title; *asterisks* mark the keyword that gets the gradient underline. */
    statement: string;
    /** Page description for search results and link previews. */
    meta: string;
    /** Two short paragraphs: who the client is and what I made for them. */
    brief: [string, string];
    delivered: [Delivered, Delivered, Delivered, Delivered];
}

export const PACKAGE_LABEL = {
    Silver: 'Silver · essentials website',
    Gold: 'Gold · maatwerk website',
    Platinum: 'Platinum · volledig op maat',
} as const;

export const projectContent: Record<string, ProjectContent> = {
    'acom-detailing': {
        place: 'Olen',
        statement: 'Een online showroom voor auto’s die er *perfect* uit moeten zien.',
        meta: 'Acom Detailing in Olen: een maatwerkwebsite met een 3D-showroom waarin bezoekers per behandeling een wagen van dichtbij bekijken.',
        brief: [
            'Acom Detailing is een detailingbedrijf in Olen, gespecialiseerd in polijstwerk, lakcorrecties en keramische coatings. Bij zulk werk draait alles om details, dus de website moest dezelfde precisie uitstralen als het resultaat.',
            'Ik bouwde een donkere, rustige site waarop de wagen de hoofdrol speelt. In plaats van een lijst met diensten scrol je door een 3D-showroom: elke behandeling krijgt zijn eigen blik op dezelfde auto.',
        ],
        delivered: [
            { title: '3D-showroom', text: 'Scrollen draait de wagen van behandeling naar behandeling: exterieur, interieur, lakcorrectie, keramische coatings en oldtimers.' },
            { title: 'Behandelingen en tarieven', text: 'Per categorie een overzicht van wat erbij hoort en wat het kost, met foto’s om te vergelijken.' },
            { title: 'Afspraak en offerte', text: 'Vanuit de hele site kan een bezoeker een afspraak maken of een offerte aanvragen.' },
            { title: 'Gemaakt voor de telefoon', text: 'Op een gsm worden de behandelingen een carrousel van kaarten, zodat het werk even sterk overkomt als op een groot scherm.' },
        ],
    },

    'roos-heylen': {
        statement: 'Een portfolio dat *authentiek* aanvoelt, net als haar content.',
        meta: 'Roos Heylen, UGC- en contentcreator: een lichte portfoliosite met een interactieve merkenwolk, uitgelicht videowerk en echte cijfers.',
        brief: [
            'Roos Heylen is een Belgische UGC- en contentcreator voor fashion-, beauty- en lifestylemerken. Haar werk is warm en persoonlijk, en een standaard portfolio paste daar niet bij.',
            'Ik bouwde een lichte, pastelkleurige site met haar foto en verhaal vooraan, en met interactieve onderdelen waarmee merken haar werk zelf kunnen ontdekken.',
        ],
        delivered: [
            { title: 'Merkenwolk', text: 'Een zwevende wolk van bubbels met de logo’s van merken waarmee ze werkte. Beweeg erover om ze te ontdekken.' },
            { title: 'Uitgelicht werk', text: 'Telefoonschermen per categorie, zoals food, fashion en lifestyle. Een klik opent de video.' },
            { title: 'Impact in cijfers', text: 'De statistieken van haar video’s naast elkaar, horizontaal te bladeren, zodat merken zien wat content oplevert.' },
            { title: 'Diensten en contact', text: 'Wat ze doet (UGC, TikTok, Instagram, foto’s) en een duidelijke weg naar een bericht, Instagram of TikTok.' },
        ],
    },

    'nerv-models': {
        place: 'Retie',
        statement: 'Een site voor een atelier dat *precies* werkt.',
        meta: 'NERV Models in Retie: een donkere, technische website voor 3D-printen, lasersnijden en prototyping, met cijfers, werkwijze en veelgestelde vragen.',
        brief: [
            'NERV Models is een eigen atelier in Retie voor prototypes en functionele onderdelen: 3D-printen, lasersnijden en ontwerp onder één dak. De klanten zijn technisch, dus de site moest helder en feitelijk zijn.',
            'Ik bouwde een strakke, donkere site met monospace-details, getallen die meteen tonen wat het atelier kan en een werkwijze in vier stappen.',
        ],
        delivered: [
            { title: 'Capaciteit in beeld', text: 'Bij het scrollen wisselt de afbeelding mee met de discipline: ontwerp en prototyping, 3D-printen en lasersnijden.' },
            { title: 'Cijfers die vertrouwen geven', text: 'Doorlooptijd 48 uur, laaghoogte 50 µm en meer dan 25 materialen: de drie getallen die een klant wil weten, bovenaan.' },
            { title: 'Werkwijze en vragen', text: 'Vier stappen van briefing tot oplevering, en een uitklapbare FAQ over bestandsformaten en het verschil tussen SLA en FDM.' },
            { title: 'Offerte binnen handbereik', text: '“Offerte” staat altijd in het menu en “Start een project” meteen bovenaan: geen zoektocht naar hoe je een aanvraag doet.' },
        ],
    },

    'bosmans-studentenkamers': {
        place: 'Diepenbeek',
        statement: 'Een *thuis* voor studenten, al voelbaar voor ze aankomen.',
        meta: 'Bosmans Studentenkamers in Diepenbeek: een warme, heldere website met de verhuurders zelf aan het woord en een eigen plek voor elk studentenhuis.',
        brief: [
            'Ben en Madeleine verhuren studentenkamers in Diepenbeek, vlak bij de campus van UHasselt. Ze willen dat elke student zich echt thuis voelt, en dat moest de site uitstralen.',
            'Ik bouwde een warme, heldere site in blauw en geel, met de beheerders zelf aan het woord en een eigen plek voor elk studentenhuis.',
        ],
        delivered: [
            { title: 'Een gezicht voor de verhuurders', text: 'Een “Wie zijn wij?”-blok waarin Ben en Madeleine zich voorstellen en zeggen waar studenten op kunnen rekenen.' },
            { title: 'Eén plek per huis', text: 'Carpe Diem en Primus hebben elk hun eigen blok met adres en meer info, bereikbaar vanuit het menu.' },
            { title: 'Direct contact', text: 'De gegevens van de beheerder staan naast een berichtformulier, zodat een vraag stellen geen drempel is.' },
            { title: 'Meteen naar de huizen', text: 'Op de startpagina brengt één duidelijke knop bezoekers rechtstreeks naar de studentenhuizen.' },
        ],
    },

    studiebooster: {
        place: 'Retie',
        statement: 'Een site die *vertrouwen* wekt bij leerlingen en bij ouders.',
        meta: 'StudieBooster van Wendy Geentjens in Retie: een heldere website voor studiebegeleiding en bijles Frans, met werkwijze, tarieven en een persoonlijk verhaal.',
        brief: [
            'StudieBooster is de studiebegeleiding van Wendy Geentjens uit Retie: coaching van leerlingen en studenten, één op één, met ook bijles Frans. Na 23 jaar in het secundair onderwijs is haar aanpak persoonlijk, dus de site moest dat ook zijn.',
            'Ik bouwde een heldere site in marineblauw en groen, met haar eigen verhaal, een duidelijke werkwijze en tarieven die niemand hoeft te zoeken.',
        ],
        delivered: [
            { title: 'Haar verhaal', text: 'Een persoonlijk deel met foto en ervaring, zodat ouders en leerlingen weten bij wie ze aankloppen.' },
            { title: 'Werkwijze en mogelijkheden', text: 'Zes kaarten, van kennismakingsgesprek en haalbare planning tot leerstrategieën en studieomgeving.' },
            { title: 'Tarieven en praktisch', text: 'Heldere kaarten met wat een oudergesprek en een coachingsessie kosten, en voor wie het bedoeld is.' },
            { title: 'Contact en route', text: 'Een berichtformulier, de contactgegevens en een knop die de route in Google Maps opent.' },
        ],
    },

    'tuinen-tuur-geerits': {
        place: 'Tessenderlo',
        statement: 'Een site waar je de *tuin* al kunt zien liggen.',
        meta: 'Tuinen Tuur Geerits in Tessenderlo: een frisse website voor tuinaanleg en tuinonderhoud, met projecten om te vergroten en een voor-en-nameschuif.',
        brief: [
            'Tuinen Tuur Geerits is een hoveniersbedrijf in Tessenderlo-Ham voor tuinaanleg, onderhoud en renovatie. Het werk spreekt voor zich, dus de site moest vooral ruimte geven aan foto’s.',
            'Ik bouwde een frisse site in groen, met beelden van echte tuinen vooraan, de diensten uitgeschreven voor Tessenderlo, Ham en omgeving en een snelle weg naar een gratis offerte.',
        ],
        delivered: [
            { title: 'Diensten per tuinklus', text: 'Tuinaanleg, onderhoud, renovatie en onkruidbestrijding, elk met een korte uitleg en geschreven voor Tessenderlo, Ham en omgeving.' },
            { title: 'Projecten om te vergroten', text: 'Een selectie recente tuinwerken in foto’s die je kunt openen, met bij elk project de vraag “Ook zo’n tuin?”.' },
            { title: 'Voor en na', text: 'Een schuif waarmee je zelf de metamorfose van een volledig gerenoveerde tuin onthult.' },
            { title: 'Offerte of afspraak', text: 'Een gratis offerte aanvragen, een afspraak maken of rechtstreeks een bericht sturen: die routes staan bovenaan, middenin en onderaan de pagina.' },
        ],
    },
};
